<template>
  <v-dialog
    v-model="dialogModel"
    max-width="1100"
    fullscreen
    scrollable
    persistent
  >
    <v-card>
      <!-- Header -->
      <v-card-title class="font-weight-medium secondary--text primary py-3">
        <v-icon class="mr-2" color="secondary">mdi-certificate-outline</v-icon>
        <span class="text-truncate">
          Consolidated Scorecard
          <span v-if="scorecard">
            - {{ scorecard.student_name }} ({{ usnId }})</span
          >
          <span v-else> - {{ usnId }}</span>
        </span>
        <v-spacer></v-spacer>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              v-bind="attrs"
              v-on="on"
              :loading="generatingPdf"
              :disabled="loading || !!error || !scorecard"
              @click="downloadAsPdf"
            >
              <v-icon small color="secondary">mdi-download</v-icon>
            </v-btn>
          </template>
          <span>Download as PDF</span>
        </v-tooltip>
        <v-btn icon small @click="close">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0" style="max-height: 78vh">
        <!-- LOADING -->
        <div v-if="loading" class="pa-4">
          <v-skeleton-loader type="card" class="mb-4"></v-skeleton-loader>
          <v-skeleton-loader
            v-for="n in 5"
            :key="n"
            type="list-item"
          ></v-skeleton-loader>
        </div>

        <!-- ERROR -->
        <div v-else-if="error" class="text-center py-12">
          <v-icon size="56" color="error lighten-1"
            >mdi-alert-circle-outline</v-icon
          >
          <div class="text-subtitle-1 font-weight-medium mt-3">
            Failed to load scorecard
          </div>
          <div class="text-body-2 grey--text mb-4">{{ error }}</div>
          <v-btn color="primary" class="white--text" @click="fetchScorecard">
            Retry
          </v-btn>
        </div>

        <!-- EMPTY -->
        <div
          v-else-if="!scorecard || subjects.length === 0"
          class="text-center py-12"
        >
          <v-icon size="56" color="grey lighten-1">mdi-school-outline</v-icon>
          <div
            class="text-subtitle-1 font-weight-medium grey--text text--darken-1 mt-3"
          >
            No results found
          </div>
          <div class="text-body-2 grey--text">
            The consolidated scorecard will appear here once results are
            published.
          </div>
        </div>

        <!-- CONTENT -->
        <v-container v-else fluid ref="printRef" class="pa-4">
          <!-- Hero header -->
          <v-card outlined class="mb-4 hover-card">
            <v-card-text class="pa-4">
              <v-row align="center" no-gutters>
                <v-col cols="12" sm="7" class="d-flex align-center">
                  <v-avatar color="primary lighten-5" size="56" class="mr-3">
                    <span class="primary--text font-weight-bold">{{
                      initials
                    }}</span>
                  </v-avatar>
                  <div>
                    <div class="text-subtitle-1 font-weight-bold primary--text">
                      {{ scorecard.student_name }}
                    </div>
                    <div class="text-caption grey--text text--darken-1">
                      {{ scorecard.usn_id }} &middot; {{ scorecard.programme }}
                    </div>
                    <div class="text-caption grey--text">
                      Latest batch: {{ scorecard.latest_batch || "-" }}
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" sm="5">
                  <v-row
                    no-gutters
                    align="center"
                    justify="end"
                    class="mt-3 mt-sm-0"
                  >
                    <v-col
                      v-if="!isAnnualProgram"
                      cols="auto"
                      class="text-center mr-6"
                    >
                      <v-progress-circular
                        :value="cgpaPercent"
                        :color="cgpaRingColor"
                        size="60"
                        width="7"
                      >
                        <span class="text-body-2 font-weight-bold">{{
                          cgpaDisplay
                        }}</span>
                      </v-progress-circular>
                      <div
                        class="text-caption font-weight-bold primary--text text-uppercase mt-1"
                      >
                        CGPA
                      </div>
                    </v-col>
                    <v-col cols="auto" class="text-center mr-6">
                      <div
                        class="text-caption font-weight-bold primary--text text-uppercase"
                      >
                        {{ isAnnualProgram ? "Year" : "Semester" }}
                      </div>
                      <div class="text-h6 font-weight-bold">
                        {{ scorecard.current_semester ?? "-" }}
                      </div>
                    </v-col>
                    <v-col cols="auto" class="text-center">
                      <div
                        class="text-caption font-weight-bold primary--text text-uppercase"
                      >
                        Earned Credits
                      </div>
                      <div class="text-h6 font-weight-bold">
                        {{ scorecard.total_credits_earned }}
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Status strip -->
            <v-row no-gutters data-pdf-ignore="true">
              <v-col
                v-for="stat in statusStrip"
                :key="stat.label"
                cols="3"
                class="text-center py-3"
                :class="stat.border"
              >
                <div
                  class="text-caption font-weight-medium grey--text text-uppercase mb-1"
                >
                  {{ stat.label }}
                </div>
                <div
                  class="text-subtitle-1 font-weight-bold"
                  :class="stat.color"
                >
                  {{ stat.value }}
                </div>
              </v-col>
            </v-row>
          </v-card>
          <!-- Semester-wise SGPA -->
          <v-card
            v-if="!isAnnualProgram && semesterSummary.length"
            outlined
            class="mb-4 hover-card"
          >
            <v-card-text class="pa-3">
              <div
                class="text-caption font-weight-bold primary--text text-uppercase mb-2"
              >
                Semester-wise SGPA
              </div>
              <div class="d-flex sgpa-scroll" style="gap: 10px">
                <div
                  v-for="sem in semesterSummary"
                  :key="`${sem.semester}-${sem.batch}`"
                  class="text-center flex-shrink-0 sgpa-pill"
                >
                  <div class="text-caption grey--text text--darken-1">
                    Sem {{ sem.semester }}
                  </div>
                  <div
                    class="text-h6 font-weight-bold"
                    :class="gpaColor(sem.sgpa) + '--text'"
                  >
                    {{ sem.sgpa != null ? Number(sem.sgpa).toFixed(2) : "-" }}
                  </div>
                  <div class="grey--text" style="font-size: 10px">
                    {{ sem.batch }}
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Filters -->
          <v-row data-pdf-ignore="true" align="center" class="mb-2" no-gutters>
            <v-col cols="12" sm="6" class="d-flex align-center">
              <v-chip-group
                v-model="statusFilterIndex"
                mandatory
                active-class="primary white--text"
              >
                <v-chip
                  v-for="opt in filterOptions"
                  :key="opt.value"
                  small
                  filter
                >
                  {{ opt.label }}
                </v-chip>
              </v-chip-group>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="search"
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                placeholder="Search course code or name"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Table -->
          <v-card outlined>
            <v-simple-table class="scorecard-table" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      {{ isAnnualProgram ? "Year" : "Semester" }}
                    </th>
                    <th class="text-left">Exam Batch</th>
                    <th class="text-left">Code</th>
                    <th class="text-left">Description</th>
                    <th class="text-center">Credit</th>
                    <th class="text-center">
                      {{ isAnnualProgram ? "Mark" : "Internal Mark" }}
                    </th>
                    <th v-if="!isAnnualProgram" class="text-center">Grade</th>
                    <th v-if="isAnnualProgram" class="text-center">Result</th>
                    <th class="text-center" data-pdf-ignore="true">History</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="row in filteredSubjects">
                    <tr :key="row.course_code">
                      <td class="font-weight-medium">
                        {{ row.subject_semester }}
                      </td>
                      <td class="font-weight-medium">{{ row.batch }}</td>
                      <td class="font-weight-medium">
                        {{ row.course_code }}
                      </td>
                      <td class="font-weight-medium">
                        {{ decodeHtml(row.course_desc) }}
                      </td>
                      <td class="text-center font-weight-medium">
                        {{ row.credit }}
                      </td>
                      <td class="text-center font-weight-medium">
                        {{
                          isAnnualProgram
                            ? row.total_mark ?? "-"
                            : row.internal_mark ?? "-"
                        }}
                      </td>
                      <td v-if="!isAnnualProgram" class="text-center">
                        <v-chip
                          x-small
                          outlined
                          :color="gradeColor(row.grade)"
                          class="font-weight-bold justify-center"
                          style="width: 32px"
                        >
                          {{ row.grade || "-" }}
                        </v-chip>
                      </td>
                      <td v-if="isAnnualProgram" class="text-center">
                        <v-chip
                          x-small
                          :color="statusColor(row.status) + ' lighten-5'"
                          :text-color="statusColor(row.status) + ' darken-2'"
                          class="font-weight-bold"
                        >
                          {{ row.result }}
                        </v-chip>
                      </td>
                      <td class="text-center" data-pdf-ignore="true">
                        <v-btn
                          v-if="row.is_reattempt"
                          x-small
                          text
                          color="amber darken-3"
                          @click="toggleHistory(row.course_code)"
                        >
                          {{ row.attempt_count }}x
                          <v-icon
                            small
                            :style="{
                              transform:
                                openHistory === row.course_code
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                            }"
                            style="transition: transform 0.15s"
                            >mdi-chevron-down</v-icon
                          >
                        </v-btn>
                        <span v-else class="grey--text text-caption">-</span>
                      </td>
                    </tr>
                    <tr
                      v-if="openHistory === row.course_code"
                      :key="row.course_code + '-history'"
                    >
                      <td
                        :colspan="isAnnualProgram ? 7 : 8"
                        class="grey lighten-4"
                      >
                        <div class="d-flex flex-wrap py-2" style="gap: 8px">
                          <v-chip
                            v-for="a in row.attempt_history"
                            :key="a.batch"
                            small
                            outlined
                            color="grey darken-1"
                          >
                            <span class="font-weight-medium mr-1">{{
                              a.batch
                            }}</span>
                            &middot;
                            <span
                              class="font-weight-bold mx-1"
                              :class="resultColorClass(a.status)"
                            >
                              {{
                                isAnnualProgram
                                  ? a.result || "-"
                                  : a.grade || "-"
                              }}
                            </span>
                            &middot;
                            <span class="ml-1"
                              >{{ a.total_mark ?? "-" }} marks</span
                            >
                          </v-chip>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!filteredSubjects.length">
                    <td
                      :colspan="isAnnualProgram ? 7 : 8"
                      class="text-center py-6 grey--text"
                    >
                      No subjects match your filters
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-container>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ConsolidatedScorecardDialog",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    // USN of the student whose scorecard should be shown
    usnId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      scorecard: null,
      loading: false,
      error: "",
      search: "",
      statusFilterIndex: 0,
      openHistory: null,
      generatingPdf: false,
      filterOptions: [
        { value: "all", label: "All" },
        { value: "PASS", label: "Passed" },
        { value: "FAIL", label: "Backlog" },
      ],
    };
  },
  computed: {
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(val) {
        if (!val) this.close();
      },
    },
    statusFilter() {
      return this.filterOptions[this.statusFilterIndex]?.value || "all";
    },
    subjects() {
      return this.scorecard?.subjects ?? [];
    },
    semesterSummary() {
      return this.scorecard?.semester_summary ?? [];
    },
    // Annual programmes carry marks + result (PASS/FAIL/ABSENT) only -
    // no grade letters, no SGPA/CGPA. Backend sends this flag directly.
    isAnnualProgram() {
      return this.scorecard?.is_annual === true;
    },
    filteredSubjects() {
      const q = (this.search || "").trim().toLowerCase();
      return this.subjects.filter((s) => {
        const matchesStatus =
          this.statusFilter === "all" || s.status === this.statusFilter;
        const matchesSearch =
          !q ||
          s.course_code?.toLowerCase().includes(q) ||
          s.course_desc?.toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
      });
    },
    initials() {
      const name = this.scorecard?.student_name ?? "";
      return (
        name
          .split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0])
          .join("")
          .toUpperCase() || "-"
      );
    },
    cgpaDisplay() {
      return Number(this.scorecard?.latest_cgpa || 0).toFixed(2);
    },
    cgpaPercent() {
      const cgpa = Math.min(Number(this.scorecard?.latest_cgpa || 0), 10);
      return (cgpa / 10) * 100;
    },
    cgpaRingColor() {
      return this.gpaColor(this.scorecard?.latest_cgpa);
    },
    statusStrip() {
      const sc = this.scorecard || {};
      return [
        {
          label: "Subjects",
          value: sc.total_subjects,
          color: "",
          border: "",
        },
        {
          label: "Passed",
          value: sc.passed_subjects,
          color: "success--text",
          border: "border-left",
        },
        {
          label: "Backlog",
          value: sc.failed_subjects,
          color: sc.failed_subjects > 0 ? "error--text" : "",
          border: "border-left",
        },
        {
          label: "Reattempted",
          value: sc.reattempted_subjects,
          color:
            sc.reattempted_subjects > 0 ? "warning--text text--darken-2" : "",
          border: "border-left",
        },
      ];
    },
  },
  watch: {
    // `immediate: true` is the important bit here: if this component is
    // rendered with `v-if="scorecardDialog"` in the parent (a common
    // pattern so the dialog isn't mounted until it's needed), it gets
    // created with `dialog` already `true`. A plain watcher never fires
    // for that initial value - only for *changes* after creation - so
    // without `immediate` the fetch silently never happens. This also
    // still covers the "always mounted, dialog toggled" pattern, since
    // `immediate` fires once on mount and the handler keeps firing on
    // every subsequent change as before.
    dialog: {
      immediate: true,
      handler(val) {
        if (val && this.usnId) {
          this.fetchScorecard();
        }
        if (!val) {
          // reset local state when the dialog closes
          this.search = "";
          this.statusFilterIndex = 0;
          this.openHistory = null;
        }
      },
    },
    // Covers the case where `usnId` arrives a tick after `dialog` goes
    // true (e.g. the parent is still resolving `studentData` when the
    // dialog opens).
    usnId(val) {
      if (val && this.dialog && !this.scorecard) {
        this.fetchScorecard();
      }
    },
  },
  methods: {
    async fetchScorecard() {
      this.loading = true;
      this.error = "";
      try {
        // NOTE: adjust this endpoint to whatever the admin-side API
        // for a single student's consolidated result is named -
        // the student-facing app calls `/my_consolidated_result`.
        const res = await this.$axios.get(`/consolidated_result/${this.usnId}`);
        this.scorecard = res.data?.data ?? res.data ?? null;
      } catch (err) {
        this.error =
          err?.response?.data?.message ??
          "Unable to fetch scorecard. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    toggleHistory(courseCode) {
      this.openHistory = this.openHistory === courseCode ? null : courseCode;
    },
    decodeHtml(str) {
      if (str == null) return str;
      const el = document.createElement("textarea");
      el.innerHTML = String(str);
      return el.value;
    },
    gpaColor(value) {
      const n = Number(value || 0);
      if (n >= 8) return "success";
      if (n >= 6) return "info";
      if (n >= 5) return "warning";
      return "error";
    },
    gradeColor(grade) {
      if (!grade) return "grey";
      const g = grade.toUpperCase();
      if (g === "S") return "teal";
      if (["A+", "A"].includes(g)) return "green";
      if (g === "B") return "blue";
      if (g === "C") return "light-blue";
      if (g === "D") return "amber darken-2";
      return "red";
    },
    statusColor(status) {
      return status === "PASS" ? "success" : "error";
    },
    resultColorClass(status) {
      return status === "PASS" ? "success--text" : "error--text";
    },
    close() {
      this.$emit("close-dialog");
    },
    async downloadAsPdf() {
      if (!this.$refs.printRef || this.generatingPdf) return;
      this.generatingPdf = true;
      try {
        // Requires html2canvas and jspdf - same libraries used by the
        // reference component. `npm install html2canvas jspdf` if not
        // already present in this Vuetify 2 project.
        const html2canvas = (await import("html2canvas")).default;
        const { default: jsPDF } = await import("jspdf");

        const el = this.$refs.printRef.$el || this.$refs.printRef;
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
          ignoreElements: (n) => n.dataset && n.dataset.pdfIgnore === "true",
        });

        const pdf = new jsPDF("p", "mm", "a4");
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const margin = 8;
        const usableW = pageW - margin * 2;
        const imgH = (canvas.height * usableW) / canvas.width;

        let heightLeft = imgH;
        let position = margin;
        const imgData = canvas.toDataURL("image/png");

        pdf.addImage(imgData, "PNG", margin, position, usableW, imgH);
        heightLeft -= pageH - margin * 2;

        while (heightLeft > 0) {
          position = heightLeft - imgH + margin;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", margin, position, usableW, imgH);
          heightLeft -= pageH - margin * 2;
        }

        pdf.save(
          `Consolidated_Scorecard_${this.scorecard?.usn_id || this.usnId}.pdf`,
        );
      } catch (err) {
        console.log(err);
        this.$store?.commit("setSnackbar", {
          message: "Failed to generate PDF",
          color: "error",
        });
      } finally {
        this.generatingPdf = false;
      }
    },
  },
};
</script>

<style scoped>
.scorecard-table >>> th {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #1976d2 !important;
}
.border-left {
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}
.sgpa-scroll {
  overflow-x: auto;
  padding-bottom: 2px;
}
.sgpa-pill {
  min-width: 68px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
